import React from 'react';

type ILeadDetailsContext = {
    isFormChanged: boolean;
    handleFormChanged: (changed: boolean) => void;
    handleChangeTab: (newTab: string) => void;
    handlePrepareChangeTab: (preparedTab?: string) => void;
    onBlockNext: () => void;
    onBlockCancel: () => void;
    tab: string;
    preparedTab?: string;
    hasUnsavedChanges: boolean;
};

const LeadDetailsContext = React.createContext<ILeadDetailsContext | undefined>(undefined);

export const useLeadDetailsContext = () => {
    const ctx = React.useContext(LeadDetailsContext);

    if (ctx === undefined) {
        throw new Error(
            `'useLeadDetailsContext' must be used within a 'LeadDetailsContextProvider'`
        );
    }

    return ctx;
};

// HWM-22633 round 3: non-throwing variant for components (LeadContactDetails)
// that mount in both v1 (no provider) and v2 (with provider) trees. Returns
// undefined when the provider isn't in scope so the caller can skip
// tab-switching gracefully.
export const useOptionalLeadDetailsContext = () => React.useContext(LeadDetailsContext);

const LeadDetailsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // SRS V4: the lead-detail page opens on the Activities tab by default.
    const [tab, setTab] = React.useState('activities');
    const [preparedTab, setPreparedTab] = React.useState<string | undefined>();
    const [isFormChanged, setFormChanged] = React.useState(false);

    const handleFormChanged = (changed: boolean) => {
        setFormChanged(changed);
    };

    const handleChangeTab = (newTab: string) => {
        setTab(newTab);
    };

    const handlePrepareChangeTab = (preparedTab?: string) => {
        setPreparedTab(preparedTab);
    };

    const onBlockNext = () => {
        preparedTab && handleChangeTab(preparedTab);
        setFormChanged(false);
        handlePrepareChangeTab(undefined);
    };

    const onBlockCancel = () => {
        setFormChanged(false);
        handlePrepareChangeTab(undefined);
    };

    return (
        <LeadDetailsContext.Provider
            value={{
                isFormChanged,
                handleFormChanged,
                tab,
                handleChangeTab,
                handlePrepareChangeTab,
                onBlockNext,
                onBlockCancel,
                preparedTab,
                hasUnsavedChanges: preparedTab !== undefined && isFormChanged
            }}
        >
            {children}
        </LeadDetailsContext.Provider>
    );
};

export default LeadDetailsProvider;
